/**
 * Плеер одного клипа для разметки (T4): волна, участки расшифровки поверх неё, пуск с
 * места клика и проигрывание одного участка — вместо браузерного <audio controls>.
 *
 * Байты клипа идут через API (у бакета нет CORS, волну по прямой ссылке не построить),
 * декодированная волна кэшируется на время сессии: раскрыть ряд второй раз — мгновенно.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import { getClipAudioBlob, type AudioUtterance } from '@/api/conversations';
import { ExactPlayer, mixDown } from '@/lib/exactPlayer';
import { subscribeRate } from '@/lib/playbackRate';
import RateButton from '@/components/RateButton';
import { Tooltip } from '@/components/ui/tooltip';

// Отсчёты держим для точного проигрывания отрезка (lib/exactPlayer.ts): 30 с при 16 кГц —
// около 2 МБ, поэтому в кэше только последние CACHE_MAX клипов.
interface Decoded { x: Float32Array; sr: number; peaks: Float32Array; dur: number }
const cache = new Map<number, Promise<Decoded>>();
const PEAKS = 1200;
const CACHE_MAX = 40;

function load(recId: number): Promise<Decoded> {
  let p = cache.get(recId);
  if (!p) {
    p = (async () => {
      const blob = await getClipAudioBlob(recId);
      const ctx = new OfflineAudioContext(1, 1, 16000);
      const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
      const x = mixDown(buf);
      const peaks = new Float32Array(PEAKS);
      const step = Math.max(1, Math.floor(x.length / PEAKS));
      for (let i = 0; i < PEAKS; i++) {
        let m = 0;
        for (let k = i * step; k < Math.min(x.length, (i + 1) * step); k++) m = Math.max(m, Math.abs(x[k]));
        peaks[i] = m;
      }
      return { x, sr: buf.sampleRate, peaks, dur: buf.duration };
    })();
    p.catch(() => cache.delete(recId));
    cache.set(recId, p);
    while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as number);
  }
  return p;
}

type Seg = AudioUtterance & { gap?: string | null; tone?: string | null };
const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;

export default function ClipPlayer({ recId, segments }: { recId: number; segments: Seg[] }) {
  const [d, setD] = useState<Decoded | null>(null);
  const [err, setErr] = useState(false);
  const [now, setNow] = useState(0);
  const [playing, setPlaying] = useState(false);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(600);

  useEffect(() => {
    let alive = true;
    setErr(false);
    load(recId).then((r) => alive && setD(r)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [recId]);
  const player = useMemo(() => (d ? new ExactPlayer(d.x, d.sr) : null), [d]);
  useEffect(() => {
    if (!player) return;
    player.onStop = (t) => { setPlaying(false); setNow(t); };
    const off = subscribeRate(() => player.applyRate());
    return () => { off(); player.onStop = undefined; player.close(); };
  }, [player]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(200, Math.floor(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dur = d?.dur ?? 30;
  const segs = useMemo(() => segments.filter((s) => s.end > s.start), [segments]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || !d) return;
    const dpr = window.devicePixelRatio || 1;
    const H = 56;
    c.width = w * dpr; c.height = H * dpr;
    const g = c.getContext('2d');
    if (!g) return;
    g.scale(dpr, dpr);
    g.clearRect(0, 0, w, H);
    const style = getComputedStyle(c);
    g.fillStyle = style.getPropertyValue('--wave') || '#94a3b8';
    const max = Math.max(1e-3, ...d.peaks);
    for (let x = 0; x < w; x++) {
      const v = d.peaks[Math.floor((x / w) * d.peaks.length)] / max;
      const h = Math.max(1, v * (H - 4));
      g.fillRect(x, (H - h) / 2, 1, h);
    }
  }, [d, w]);

  useEffect(() => {
    if (!playing || !player) return;
    let raf = 0;
    const tick = () => { setNow(player.position()); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, player]);

  // Отрезок — ровно от начала до конца, без перемотки и паузы по часам (lib/exactPlayer.ts).
  const play = (a: number, b: number | null = null) => {
    if (!player) return;
    player.play(a, b); setNow(a); setPlaying(true);
  };
  const toggle = () => {
    if (!player) return;
    if (player.playing) player.pause();
    else { player.play(player.position() >= dur - 0.02 ? 0 : player.position(), null); setPlaying(true); }
  };

  const x = (t: number) => (t / dur) * w;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs">
        <button type="button" onClick={toggle} disabled={!d}
          className="inline-flex items-center gap-1 rounded border px-2 py-0.5 disabled:opacity-50">
          {playing ? <Pause className="size-3" /> : <Play className="size-3" />} {fmt(now)} / {fmt(dur)}
        </button>
        <RateButton />
        {!d && !err && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
        {err && <span className="text-muted-foreground">звук клипа не загрузился</span>}
      </div>
      <div ref={box} className="relative h-14 w-full cursor-pointer select-none overflow-hidden rounded border bg-card [--wave:#94a3b8]"
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); play(((e.clientX - r.left) / r.width) * dur); }}>
        {segs.map((s, i) => (
          <Tooltip key={i} content={`${fmt(s.start)}–${fmt(s.end)} ${s.text || s.gap || s.tone || ''}`}><div
            onClick={(e) => { e.stopPropagation(); play(s.start, s.end); }}
            className={`absolute inset-y-0 ${s.tone ? 'bg-amber-400/35' : s.gap ? 'bg-foreground/5' : 'bg-sky-500/15 hover:bg-sky-500/30'}`}
            style={{ left: x(s.start), width: Math.max(2, x(s.end) - x(s.start)) }} /></Tooltip>
        ))}
        <canvas ref={canvas} className="pointer-events-none absolute inset-0" style={{ width: w, height: 56 }} />
        {d && <div className="pointer-events-none absolute inset-y-0 w-px bg-primary" style={{ left: x(now) }} />}
      </div>
    </div>
  );
}
